// =============================================================================
// authDirect — GoTrue calls made with raw fetch, on purpose.
//
// supabase-js auth calls (signInWithPassword, verifyOtp, setSession, …) go
// through navigator.locks. That same lock hangs tenant-switching and the
// AcceptInvite save when banner / session-refresh queries fire on first
// paint, which is why Login.tsx signs in over REST and AcceptInvite saves the
// password with a raw PUT. This module is the shared half of that pattern:
// exchange a credential over REST, write the session into localStorage in
// supabase-js v2's shape, and let the caller reload — supabase-js rehydrates
// from disk on the next boot with no lock involvement.
// =============================================================================

const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL  as string
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string

interface DirectAuthTokens {
  access_token:  string
  refresh_token: string
  expires_in?:   number
  expires_at?:   number
  token_type?:   string
  user?:         unknown
}

type GoTrueError = { msg?: string; error_description?: string; error?: string } | null

/** The localStorage key supabase-js v2 reads the session from. */
function sessionStorageKey(): string {
  // Project ref from https://<ref>.supabase.co → sb-<ref>-auth-token
  const ref = new URL(SUPABASE_URL).host.split('.')[0]
  return `sb-${ref}-auth-token`
}

/** Persist a session the way supabase-js v2 expects to find it. */
function storeDirectSession(tokens: DirectAuthTokens): void {
  const session = {
    access_token:  tokens.access_token,
    token_type:    tokens.token_type   ?? 'bearer',
    expires_in:    tokens.expires_in   ?? 3600,
    expires_at:    tokens.expires_at   ?? Math.floor(Date.now() / 1000) + 3600,
    refresh_token: tokens.refresh_token,
    user:          tokens.user ?? null,
  }
  try {
    window.localStorage.setItem(sessionStorageKey(), JSON.stringify(session))
  } catch (storageErr) {
    console.warn('storeDirectSession: localStorage write failed', storageErr)
  }
}

async function gotrueError(resp: Response, fallback: string): Promise<string> {
  const body = await resp.json().catch(() => null) as GoTrueError
  return body?.error_description ?? body?.msg ?? body?.error ?? fallback
}

/** Password sign-in over REST. Stores the session on success. */
export async function signInDirect(email: string, password: string): Promise<{ error?: string }> {
  try {
    const resp = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON },
      body:    JSON.stringify({ email, password }),
    })
    if (!resp.ok) return { error: await gotrueError(resp, `Sign-in failed (${resp.status})`) }
    storeDirectSession(await resp.json() as DirectAuthTokens)
    return {}
  } catch (e) {
    return { error: (e as Error).message }
  }
}

export type TokenHashType = 'invite' | 'recovery'

/**
 * Exchange a hand-over link's token_hash for a session
 * (docs/specs/no-mailbox-logins.md §3.4). Called ONLY from the interstitial's
 * button, never on page load — a link preview or Safe-Links prefetch of the
 * URL must burn nothing. Stores the session on success; the caller reloads.
 *
 * `expired` is the one-time token being used, expired or unknown — the
 * "ask your admin for a new link" case, distinct from a network failure.
 */
export async function verifyTokenHashDirect(
  tokenHash: string, type: TokenHashType,
): Promise<{ ok: true } | { ok: false; expired: boolean; error: string }> {
  try {
    const resp = await fetch(`${SUPABASE_URL}/auth/v1/verify`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON },
      body:    JSON.stringify({ type, token_hash: tokenHash }),
    })
    if (!resp.ok) {
      const error = await gotrueError(resp, `Link could not be verified (${resp.status})`)
      // GoTrue: 403 otp_expired / 401 for a consumed or unknown token.
      return { ok: false, expired: resp.status === 401 || resp.status === 403 || resp.status === 404, error }
    }
    storeDirectSession(await resp.json() as DirectAuthTokens)
    return { ok: true }
  } catch (e) {
    return { ok: false, expired: false, error: (e as Error).message }
  }
}

// -----------------------------------------------------------------------------
// Sign in with Google / Microsoft — PKCE over REST, for the same reason as
// signInDirect: we redirect, exchange the code ourselves, store the session in
// supabase-js's shape and reload.
//
//   start     GET  /auth/v1/authorize?provider=…&code_challenge=…   (full page)
//   return    /auth/callback?code=…   (or ?error=…)       → AuthCallback.tsx
//   exchange  POST /auth/v1/token?grant_type=pkce {auth_code, code_verifier}
//
// The verifier lives under OUR key, not supabase-js's `<storageKey>-code-verifier`:
// supabase-js only treats `?code=` as its own callback when its key is present,
// so it never races us for the single-use code.
//
// Only EXISTING accounts can sign in this way. The before_user_created hook
// (mig 20261034000000) refuses a new user from any provider but email, so a
// Google account that matches nobody gets a refusal, not an empty account.
// -----------------------------------------------------------------------------

/** GoTrue's provider keys. 'azure' is Microsoft: work/school AND personal accounts. */
export type OAuthProvider = 'google' | 'azure'

const OAUTH_PROVIDERS: readonly OAuthProvider[] = ['google', 'azure']

export const OAUTH_PROVIDER_NAME: Record<OAuthProvider, string> = {
  google: 'Google',
  azure:  'Microsoft',
}

/** The custom auth domain when there is one (provider screens then name our
 *  domain, not the project ref), else the project URL. Never used for the
 *  session's storage key — that stays on VITE_SUPABASE_URL, see .env.example. */
const AUTH_URL = import.meta.env.VITE_SUPABASE_AUTH_URL || SUPABASE_URL

const OAUTH_VERIFIER_KEY = 'kf49.oauth-pkce'

/** The path the provider returns to. Every app origin's copy of it must be on
 *  the Supabase redirect allow-list, or GoTrue silently sends the user to the
 *  Site URL instead and the code is lost. */
const OAUTH_CALLBACK_PATH = '/auth/callback'

function base64Url(bytes: Uint8Array): string {
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Which providers the project has switched on, from GoTrue's public settings.
 *  The Login page shows a button only for these, so a provider that is not
 *  configured yet is never offered (its /authorize answers with raw JSON). */
export async function fetchEnabledOAuthProviders(signal?: AbortSignal): Promise<OAuthProvider[]> {
  const resp = await fetch(`${AUTH_URL}/auth/v1/settings`, { headers: { apikey: SUPABASE_ANON }, signal })
  if (!resp.ok) return []
  const body = await resp.json() as { external?: Record<string, boolean> }
  return OAUTH_PROVIDERS.filter(p => body.external?.[p] === true)
}

/** Leave for the provider's sign-in page. Resolves only if the redirect could
 *  not be started; on success the page is already navigating away. */
export async function startOAuthDirect(provider: OAuthProvider): Promise<{ error?: string }> {
  try {
    const verifier  = base64Url(crypto.getRandomValues(new Uint8Array(48)))
    const digest    = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
    const challenge = base64Url(new Uint8Array(digest))
    // localStorage, not sessionStorage: the provider may hand the return leg to
    // a fresh browsing context (an installed app's in-app browser), and the
    // verifier is useless without the single-use code anyway.
    window.localStorage.setItem(OAUTH_VERIFIER_KEY, verifier)

    const params = new URLSearchParams({
      provider,
      redirect_to:           `${window.location.origin}${OAUTH_CALLBACK_PATH}`,
      code_challenge:        challenge,
      code_challenge_method: 's256',
      // A shared office PC must not sign the next person in as whoever used
      // Google there last. GoTrue forwards it to the provider.
      prompt:                'select_account',
    })
    // Microsoft sends no email unless asked, and an account without one
    // cannot be matched to anybody.
    if (provider === 'azure') params.set('scopes', 'email')

    window.location.assign(`${AUTH_URL}/auth/v1/authorize?${params}`)
    return {}
  } catch (e) {
    return { error: (e as Error).message }
  }
}

export type OAuthExchangeResult =
  | { ok: true }
  /** no_verifier: this browser never started the sign-in (another browser or
   *  app, or the code was already spent here). rejected: GoTrue refused the
   *  code — expired or used. network: never reached GoTrue; safe to retry. */
  | { ok: false; reason: 'no_verifier' | 'rejected' | 'network'; error: string }

/** Swap the provider's code for a session. Stores it on success; the caller reloads. */
export async function exchangeOAuthCodeDirect(code: string): Promise<OAuthExchangeResult> {
  let verifier: string | null = null
  try { verifier = window.localStorage.getItem(OAUTH_VERIFIER_KEY) } catch { /* storage blocked */ }
  if (!verifier) return { ok: false, reason: 'no_verifier', error: 'No sign-in was started in this browser' }

  let resp: Response
  try {
    resp = await fetch(`${AUTH_URL}/auth/v1/token?grant_type=pkce`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON },
      body:    JSON.stringify({ auth_code: code, code_verifier: verifier }),
    })
  } catch (e) {
    // Keep the verifier: the code may still be unspent, so Try again can work.
    return { ok: false, reason: 'network', error: (e as Error).message }
  }
  // GoTrue answered. On success the code is spent; on a refusal this verifier
  // can never redeem it (wrong pair, or the 5-minute flow expired). Drop it —
  // the worst a crafted link can do is make the user start again.
  try { window.localStorage.removeItem(OAUTH_VERIFIER_KEY) } catch { /* storage blocked */ }
  if (!resp.ok) return { ok: false, reason: 'rejected', error: await gotrueError(resp, `Sign-in could not be completed (${resp.status})`) }
  storeDirectSession(await resp.json() as DirectAuthTokens)
  return { ok: true }
}
